import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { graphql, useFragment, type PreloadedEntryPoint } from 'react-relay';
import { useShowRoadmapsUpsell as useShowRoadmapsUpsellNew } from '@jira/growth__jira-roadmap-in-nav/src/useShowRoadmapsUpsell.tsx';
import type { useShowRoadmapsUpsell$key } from '@jira/growth__jira-roadmap-in-nav/src/__generated__/useShowRoadmapsUpsell.graphql.ts';
import { isTeamsAppEnabled } from '@jira/platform__teams-app-config/src/index.tsx';
import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';
import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts/types';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { getConfigurableMenuIds } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-configurable-menu-ids/index.tsx';
import {
	getAppItemsData,
	getProductItemsData,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/items-data/index.tsx';

import { useGlobalApps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/global-apps/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';

import { ConfigurableMenuItem } from '../../common/ui/configurable-menu-item/ConfigurableMenuItem.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '../../controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import { useRefreshL1MenuItems } from '../../controllers/use-refresh-l1-menu-items/index.tsx';
import { OperationsDiscoverabilitySpotlightForAdmins } from './OperationsDiscoverabilitySpotlightForAdmins.tsx';
import type { ConfigurableMenuItems$key } from './__generated__/ConfigurableMenuItems.graphql';
import { MoreMenuIsOpenContextProvider } from './more-menu-is-open-context-provider/index.tsx';
import { SidebarCustomization } from './sidebar-customization/SidebarCustomization.tsx';
import { PeekMenuIdContextProvider } from './peek-menu-id-context-provider/index.tsx';
import {
	GlobalShortcuts,
	GlobalShortcutsWithInvitePeopleProvider,
} from './global-shortcuts/index.tsx';
import type { GlobalShortcutsInvitedAppShortcutsRecommendationsComponent } from './global-shortcuts/invited-app-shortcuts-recommendations/GlobalShortcutsInvitedAppShortcutsRecommendations.tsx';
import { useRenderableShortcutMenuIds } from './global-shortcuts/use-renderable-shortcut-menu-ids/useRenderableShortcutMenuIds.tsx';

const useShowRoadmapsUpsell = functionWithCondition(
	() => fg('jpd-roadmaps-in-jira'),
	(data: useShowRoadmapsUpsell$key) => useShowRoadmapsUpsellNew(data),
	() => false,
);

const useRenderableShortcutMenuIdsWithFg = functionWithCondition(
	() => fg('navx-4531-gas-customisation-fix'),
	(teamsSupported: boolean) => useRenderableShortcutMenuIds(teamsSupported),
	(_teamsSupported: boolean) => [],
);

export type ConfigurableMenuItemsProps = {
	invitedAppShortcutsRecommendationsEntryPoint?: PreloadedEntryPoint<GlobalShortcutsInvitedAppShortcutsRecommendationsComponent>;
	menuData: ConfigurableMenuItems$key;
};

export const ConfigurableMenuItems: React.MemoExoticComponent<typeof ConfigurableMenuItemsInner> =
	memo(ConfigurableMenuItemsInner);

function ConfigurableMenuItemsInner({
	menuData,
	invitedAppShortcutsRecommendationsEntryPoint,
}: ConfigurableMenuItemsProps): React.JSX.Element {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<ConfigurableMenuItems$key>(
		graphql`
			fragment ConfigurableMenuItems on Query {
				jira @optIn(to: "JiraUserNavConfig") {
					userNavigationConfiguration(cloudID: $cloudId, navKey: "NAV_SIDEBAR") {
						navItems {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							menuId @required(action: THROW)
							# eslint-disable-next-line @atlassian/relay/unused-fields
							isVisible @required(action: THROW)
						}
					}
				}
				...ConfigurableMenuItem
				...useShowRoadmapsUpsell @dangerously_unaliased_fixme @include(if: $isRoadmapUpsellEnabled)
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		menuData,
	);

	// Creates a synced ref to the global app shortcuts to get the response from global app shortcuts component.
	// const getFilteredProductLinks = useRef<fnGetFilteredProductLinks | undefined>(undefined);
	const [getFilteredProductLinks, setGetFilteredProductLinks] = useState<
		fnGetFilteredProductLinks | undefined
	>(undefined);

	const globalAppLicense = useGlobalApps();

	const shouldShowRoadmapsUpsell = useShowRoadmapsUpsell(data);

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		// Add back any missing menu ids before filtering in case licensing has changed or a new L1 has been created.
		// This also handles when a user has no configuration yet.
		useRefreshL1MenuItems(data.jira?.userNavigationConfiguration?.navItems),
		{ shouldShowRoadmapsUpsell },
	);

	const navItemsContext = useMemo(
		() => ({
			navItems,
			visibleMenuIds: getConfigurableMenuIds(navItems, true),
			moreMenuIds: getConfigurableMenuIds(navItems, false),
			productItemsData: getProductItemsData(navItems),
		}),
		[navItems],
	);

	const appItemsDataUnstable = useMemo(
		() => getAppItemsData(navItems, globalAppLicense, getFilteredProductLinks),
		[globalAppLicense, navItems, getFilteredProductLinks],
	);

	/**
	 * Unfortunately appItemsDataUnstable is not stable, as setGetFilteredProductLinks gets called later to update getFilteredProductLinks.
	 * However, even when getFilteredProductLinks changes, appItemsData does not always change meaningfully (eg same data but new instantiation).
	 * This means that we can use a stable ref to avoid unnecessary re-renders of the GlobalShortcuts component.
	 */
	const [appItemsDataStable, setAppItemsDataStable] = useState(appItemsDataUnstable);
	useEffect(() => {
		if (!isEqual(appItemsDataStable, appItemsDataUnstable)) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setAppItemsDataStable(appItemsDataUnstable);
		}
	}, [appItemsDataStable, appItemsDataUnstable]);

	const handleUpdateGetFilteredProductLinks = useCallback((fn: fnGetFilteredProductLinks) => {
		setGetFilteredProductLinks(() => fn);
	}, []);

	const teamsEnabled = isTeamsAppEnabled();

	const renderableShortcutMenuIds = useRenderableShortcutMenuIdsWithFg(teamsEnabled);
	const gatedRenderableMenuIds = fg('navx-4531-gas-customisation-fix')
		? renderableShortcutMenuIds
		: null;

	const modalAppItemsData = useMemo(() => {
		if (gatedRenderableMenuIds === null) {
			return appItemsDataStable;
		}
		const renderable = new Set(gatedRenderableMenuIds);
		return appItemsDataStable.filter((item) => renderable.has(item.menuId));
	}, [appItemsDataStable, gatedRenderableMenuIds]);

	// Teams App requires the invite modal context provider to be wrapped around it.
	const globalShortcuts = useMemo(() => {
		return (
			<>
				<TopLevelSpacer />
				{teamsEnabled ? (
					<GlobalShortcutsWithInvitePeopleProvider
						appItemsData={appItemsDataStable}
						handleUpdateGetFilteredProductLinks={handleUpdateGetFilteredProductLinks}
						invitedAppShortcutsRecommendationsEntryPoint={
							invitedAppShortcutsRecommendationsEntryPoint
						}
					/>
				) : (
					<GlobalShortcuts
						appItemsData={appItemsDataStable}
						handleUpdateGetFilteredProductLinks={handleUpdateGetFilteredProductLinks}
						invitedAppShortcutsRecommendationsEntryPoint={
							invitedAppShortcutsRecommendationsEntryPoint
						}
					/>
				)}
			</>
		);
	}, [
		appItemsDataStable,
		handleUpdateGetFilteredProductLinks,
		invitedAppShortcutsRecommendationsEntryPoint,
		teamsEnabled,
	]);

	const getAppShortcutURL = useCallback(
		(apsKey: string) => {
			if (getFilteredProductLinks) {
				const result = getFilteredProductLinks(apsKey);
				return result.links[0]?.url;
			}
		},
		[getFilteredProductLinks],
	);

	const { push } = useRouterActions();

	return (
		<NavItemsContext.Provider value={navItemsContext}>
			<PeekMenuIdContextProvider>
				<MoreMenuIsOpenContextProvider>
					{fg('ops-overview-disc-spotlights') && (
						<OperationsDiscoverabilitySpotlightForAdmins
							onClick={() => push('/jira/ops/overview')}
						/>
					)}
					{navItemsContext.visibleMenuIds.map((menuId) => (
						<ConfigurableMenuItem key={menuId} menuData={data} menuId={menuId} />
					))}
					{globalShortcuts}
					<TopLevelSpacer />
					<SidebarCustomization
						navItems={navItems}
						menuData={data}
						productItemsData={navItemsContext.productItemsData}
						appItemsData={modalAppItemsData}
						getAppShortcutURL={getAppShortcutURL}
					/>
				</MoreMenuIsOpenContextProvider>
			</PeekMenuIdContextProvider>
		</NavItemsContext.Provider>
	);
}
